/// <reference types="@angular/localize" />

/*
 * Entry point of the application.
 * Only platform bootstrapping code should be here.
 * For app-specific initialization, use `app/app.component.ts`.
 */

import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { provideOptimus } from '@openng/optimus-ui/config';
import Aura from '@openng/optimus-ui-themes/aura';
import { Amplify } from 'aws-amplify';

import { AppModule } from '@app/app.module';
import { loadCognitoRuntimeConfig } from '@app/auth/cognito-config';
import { environment } from '@env/environment';

if (environment.production) {
  enableProdMode();
}

function showStartupError(error: unknown): void {
  console.error('Application startup failed', error);

  const main = document.createElement('main');
  main.setAttribute('role', 'alert');
  main.style.cssText = 'max-width: 48rem; margin: 3rem auto; padding: 1.5rem; font-family: sans-serif;';

  const heading = document.createElement('h1');
  heading.textContent = 'Unable to start the data portal';

  const message = document.createElement('p');
  message.textContent =
    'The application could not load its authentication configuration. If the problem persists, please contact support.';

  const details = document.createElement('details');
  const summary = document.createElement('summary');
  summary.textContent = 'Technical details';

  const technicalDetails = document.createElement('pre');
  technicalDetails.textContent = error instanceof Error ? error.message : String(error);

  details.append(summary, technicalDetails);
  main.append(heading, message, details);
  document.body.replaceChildren(main);
}

async function startApplication(): Promise<void> {
  try {
    const cognitoConfig = await loadCognitoRuntimeConfig(environment.serverUrl);
    Amplify.configure(cognitoConfig);

    await platformBrowserDynamic().bootstrapModule(AppModule, {
      applicationProviders: [
        provideZoneChangeDetection(),
        provideOptimus({
          theme: {
            preset: Aura,
            options: { darkModeSelector: '.dark' },
          },
        }),
      ],
    });
  } catch (error) {
    showStartupError(error);
  }
}

void startApplication();
